import { FC, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useOutletContext } from 'react-router-dom';
import {
  DataRoomActivityRow,
  DataRoomActivityWindow,
  DataRoomStatus,
  DEFAULT_DATA_ROOM_ACTIVITY_WINDOW,
  formatViewingTime,
} from 'shared';
import { twJoin } from 'tailwind-merge';

import {
  ChevronDownIcon,
  SlashCircleIcon,
  SuccessCircleIcon,
} from '../../../../assets/icons/index';
import dataRoomGlobeIcon from '../../../../assets/images/data-room-activity/data-room-globe.svg';
import { AppFormattedMessage } from '../../../../components/app-formatted-message/index';
import { TableRow } from '../../../../components/table/index';
import { Tag, TagVariant } from '../../../../components/tag/index';
import { StringKey } from '../../../../lang/index';
import { PageRoute } from '../../../../types/page.types';
import { LastActive } from '../../workspaces/components/last-active';
import { ActivityEmptyState } from '../components/activity-empty-state';
import { ActivityLoadError } from '../components/activity-load-error';
import { ActivityTablePlaceholder } from '../components/activity-loading-placeholders';
import { ActivityPagedTable } from '../components/activity-paged-table';
import { ActivityWindowToggle } from '../components/activity-window-toggle';
import { useActivityDataRooms } from '../hooks/use-activity-data-rooms.hook';
import { ActivityOutletContext } from '../types/activity-outlet-context.types';

enum Column {
  DATA_ROOM = 'dataRoom',
  DOWNLOADS = 'downloads',
  LAST_ACTIVITY = 'lastActivity',
  OPEN = 'open',
  STATUS = 'status',
  VIEWING_TIME = 'viewingTime',
  VIEWS = 'views',
  VISITORS = 'visitors',
}

// 20.1.2 §2, in the order the story lists them, then the arrow that opens the room.
const COLUMN_ORDER = [
  Column.DATA_ROOM,
  Column.STATUS,
  Column.VISITORS,
  Column.VIEWS,
  Column.DOWNLOADS,
  Column.VIEWING_TIME,
  Column.LAST_ACTIVITY,
  Column.OPEN,
];

const COLUMN_TITLE: Record<Exclude<Column, Column.OPEN>, StringKey> = {
  [Column.DATA_ROOM]: 'DATA_ROOM',
  [Column.DOWNLOADS]: 'DOWNLOADS',
  [Column.LAST_ACTIVITY]: 'LAST_ACTIVITY',
  [Column.STATUS]: 'STATUS',
  [Column.VIEWING_TIME]: 'VIEWING_TIME',
  [Column.VIEWS]: 'VIEWS',
  [Column.VISITORS]: 'VISITORS',
};

const COLUMNS_TITLE = {
  ...Object.fromEntries(
    Object.entries(COLUMN_TITLE).map(([column, title]) => [
      column,
      <AppFormattedMessage id={title} key={column} />,
    ]),
  ),
  [Column.OPEN]: null,
};

const StatusTag: FC<{ status: DataRoomStatus }> = ({ status }) =>
  status === DataRoomStatus.ACTIVE ? (
    <Tag
      icon={<SuccessCircleIcon iconColor="#039855" />}
      title={<AppFormattedMessage id="ACTIVE" />}
      variant={TagVariant.GREEN}
    />
  ) : (
    <Tag
      className="bg-gray-50 text-gray-600"
      icon={<SlashCircleIcon iconColor="#475467" />}
      title={<AppFormattedMessage id="INACTIVE" />}
      variant={TagVariant.GRAY}
    />
  );

const DataRoomName: FC<{ name: string }> = ({ name }) => (
  <span className="flex min-w-0 items-center gap-3">
    <span className="flex size-9 shrink-0 items-center justify-center rounded-lg border border-gray-100 bg-white">
      <img alt="" className="size-6" src={dataRoomGlobeIcon} />
    </span>
    <span className="truncate">{name}</span>
  </span>
);

/**
 * The Data Rooms sub-tab — 20.1.2 §1 to §4.
 *
 * Every number on a row is the window's except the room itself; a room nobody opened is still
 * here with zeros (§4), and the order is Active first, then most recently active (§3). All of that
 * is decided by the read layer — this draws what it returns and sorts nothing of its own.
 */
export const ActivityDataRoomsTab: FC = () => {
  const navigate = useNavigate();
  const { toolbar } = useOutletContext<ActivityOutletContext>();
  // §3: this tab's window, independent of the one on Overview.
  const [window, setWindow] = useState<DataRoomActivityWindow>(DEFAULT_DATA_ROOM_ACTIVITY_WINDOW);
  const { count, isError, isPending, pagination, retry, rows } = useActivityDataRooms({ window });

  const openRoom = (row: DataRoomActivityRow): void =>
    navigate(PageRoute.DATA_ROOM_ACTIVITY_ROOM.replace(':dataRoomId', row.dataRoomId));

  const renderContent = () => {
    if (isPending) {
      return <ActivityTablePlaceholder columns={7} />;
    }

    if (isError) {
      return <ActivityLoadError onRetry={() => void retry()} />;
    }

    // §4: only a company with no rooms is empty here; a quiet range still lists every room, with
    // zeros.
    if (count === 0) {
      return (
        <div className="flex h-full min-h-0 w-full flex-1 flex-col items-center">
          <ActivityEmptyState description="NO_ACTIVITY_YET_HINT" title="NO_ACTIVITY_YET" />
        </div>
      );
    }

    return (
      // Fades in on arrival; a new window dims it while its first page loads.
      <div
        className={twJoin(
          'flex h-full min-h-0 w-full flex-1 animate-reveal-fade flex-col gap-4 overflow-y-auto p-4 transition-opacity duration-200',
          pagination.isReloading && 'opacity-60',
        )}
      >
        <ActivityPagedTable
          className="min-w-[1000px]"
          columnOrder={COLUMN_ORDER}
          columnsTitle={COLUMNS_TITLE}
          pagination={pagination}
          renderRow={(row, className) => (
            <TableRow
              columnOrder={COLUMN_ORDER}
              contentClassName={{ general: 'gap-3 px-4', [Column.OPEN]: 'justify-center px-2' }}
              fields={{
                id: row.dataRoomId,
                [Column.DATA_ROOM]: <DataRoomName name={row.name} />,
                [Column.STATUS]: <StatusTag status={row.status} />,
                [Column.VISITORS]: row.uniqueVisitors,
                [Column.VIEWS]: row.views,
                [Column.DOWNLOADS]: row.downloads,
                [Column.VIEWING_TIME]: formatViewingTime(row.viewingTimeMs),
                // 20.1.0 §9, by the same rule People with Access already reads.
                [Column.LAST_ACTIVITY]: <LastActive at={row.lastActivityAt} />,
                [Column.OPEN]: <ChevronDownIcon className="-rotate-90" iconColor="#475467" />,
              }}
              key={row.dataRoomId}
              labelClassName="min-w-0 text-sm"
              onClick={() => openRoom(row)}
              tdClassName="p-0"
              trClassName={twJoin(
                'cursor-pointer transition-colors duration-150 hover:bg-gray-50',
                className,
              )}
            />
          )}
          rows={rows}
          tHeadClassNames={{
            thClassName: {
              // The room's name takes what is left; every other column is sized to what it holds.
              general: 'px-4',
              [Column.DOWNLOADS]: 'w-[120px]',
              [Column.LAST_ACTIVITY]: 'w-[160px]',
              [Column.OPEN]: 'w-[54px]',
              [Column.STATUS]: 'w-32',
              [Column.VIEWING_TIME]: 'w-[136px]',
              [Column.VIEWS]: 'w-28',
              [Column.VISITORS]: 'w-28',
            },
          }}
        />
      </div>
    );
  };

  return (
    <>
      {/* Drawn beside the sub-tab buttons, but the window it sets stays this tab's own. */}
      {toolbar &&
        createPortal(<ActivityWindowToggle onChange={setWindow} value={window} />, toolbar)}
      {renderContent()}
    </>
  );
};
